import type { LandscapeId } from "@/features/story/data/landscapes";

export type LandCoverMapYear = 2005 | 2015 | 2025;

export type LandCoverMapAsset = {
  readonly landscapeId: LandscapeId;
  readonly year: LandCoverMapYear;
  /** Filename inside `public/Occupation du sol/`. */
  readonly fileName: string;
  readonly title: string;
};

const FOLDER = "Occupation du sol";

/**
 * Canonical occupation-du-sol map plates shipped under `public/Occupation du sol`.
 * Pixel-perfect source maps from the study (Landsat / terrain).
 */
export const LAND_COVER_MAP_ASSETS: readonly LandCoverMapAsset[] = [
  {
    landscapeId: "madina-oula",
    year: 2005,
    fileName: "ocs madina-oula 2005.png",
    title: "Occupation du sol Madina-Oula 2005",
  },
  {
    landscapeId: "madina-oula",
    year: 2015,
    fileName: "ocs madina-oula 2015.png",
    title: "Occupation du sol Madina-Oula 2015",
  },
  {
    landscapeId: "madina-oula",
    year: 2025,
    fileName: "ocs madina-oula2025.png",
    title: "Occupation du sol Madina-Oula 2025",
  },
  {
    landscapeId: "kounounkan",
    year: 2005,
    fileName: "occupation du sol Parc de Kounounkan 2005.png",
    title: "Occupation du sol du Parc de Kounounkan 2005",
  },
  {
    landscapeId: "kounounkan",
    year: 2015,
    fileName: "occupation du sol Parc de Kounounkan 2015.png",
    title: "Occupation du sol du Parc de Kounounkan 2015",
  },
  {
    landscapeId: "kounounkan",
    year: 2025,
    fileName: "occupation du sol Parc de Kounounkan 2025.png",
    title: "Occupation du sol du Parc de Kounounkan 2025",
  },
  {
    landscapeId: "allassoya-moussaya",
    year: 2005,
    fileName: "occupation du sol de Moussaya et Allasonyah 2005.png",
    title: "Occupation du sol Allansonyah et Moussayah 2005",
  },
  {
    landscapeId: "allassoya-moussaya",
    year: 2015,
    fileName: "occupation du sol de Moussaya et Allasonyah 2015.png",
    title: "Occupation du sol Allansonyah et Moussayah 2015",
  },
  {
    landscapeId: "allassoya-moussaya",
    year: 2025,
    fileName: "occupation du sol de Moussaya et Allasonyah 2025.png",
    title: "Occupation du sol Allansonyah et Moussayah 2025",
  },
] as const;

export const LAND_COVER_MAP_YEARS: readonly LandCoverMapYear[] = [2005, 2015, 2025];

/** Public URL for a plate (spaces encoded). */
export function landCoverMapSrc(fileName: string): string {
  return `/${encodeURIComponent(FOLDER)}/${encodeURIComponent(fileName)}`;
}

export function landCoverMapsForLandscape(landscapeId: LandscapeId): readonly LandCoverMapAsset[] {
  return LAND_COVER_MAP_ASSETS.filter((a) => a.landscapeId === landscapeId);
}

/** Prefer exact year; otherwise nearest available plate for that landscape. */
export function resolveLandCoverMap(
  landscapeId: LandscapeId,
  year: number,
): { asset: LandCoverMapAsset | null; exact: boolean } {
  const available = landCoverMapsForLandscape(landscapeId);
  if (available.length === 0) return { asset: null, exact: false };
  const exact = available.find((a) => a.year === year);
  if (exact) return { asset: exact, exact: true };
  const sorted = [...available].sort((a, b) => Math.abs(a.year - year) - Math.abs(b.year - year));
  return { asset: sorted[0] ?? null, exact: false };
}

/** Stable colors aligned with the study legend plates. */
export function landCoverClassColor(label: string): string {
  const key = label.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
  if (key.includes("galerie")) return "#1aa6a0";
  if (key.includes("mangrove")) return "#b000a8";
  if (key.includes("clair") || key.includes("foret")) return "#1f5c28";
  if (key.includes("herbeuse")) return "#7dcf2e";
  if (key.includes("savane") || key.includes("arbore") || key.includes("arbust")) {
    return "#a8c97a";
  }
  if (key.includes("agricole") || key.includes("culture")) return "#f0d000";
  if (key.includes("habite")) return "#e02020";
  if (key.includes("humide") || key.includes("eau")) return "#2a5fd4";
  if (
    key.includes("routier") ||
    key.includes("ferroviaire") ||
    key.includes("sols nus") ||
    key.includes("affleurement") ||
    key.includes("route")
  ) {
    return "#e8b896";
  }
  return "#6b8f71";
}
